import { IS_DEBUG } from '@dtx-company/true-common/src/constants/env'
import {
  OptimizelyDecideOption,
  eventDispatcher as _eventDispatcher,
  createInstance,
  enums
} from '@optimizely/react-sdk'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useCookieState } from '../../redux/selectors/useCookieState'
import { useCurrentUserJwt } from '../../redux/selectors/currentUserSelectors'
import { useHeapSessionId } from '@dtx-company/true-common/src/utils/cookies/cookies.hooks'
import { useIsMounted } from '@dtx-company/true-common/src/hooks/useIsMounted'
import { useOptimizelyContext } from '../useOptimizelyContext'
import type { EventV1 as OptimizelyEvent } from '@optimizely/js-sdk-event-processor'
export type EventDispatcher = typeof _eventDispatcher
type EventDispatcherArgs = Parameters<typeof _eventDispatcher.dispatchEvent>
export type OptimizelyReactSDKClient = ReturnType<typeof createInstance> | undefined

const useCreateOptimizely = (): ((
  datafile: string,
  eventDispatcher: EventDispatcher
) => OptimizelyReactSDKClient) => {
  const isMounted = useIsMounted()
  return useCallback(
    (datafile, eventDispatcher) => {
      const defaultDecideOptions = [
        OptimizelyDecideOption.ENABLED_FLAGS_ONLY,
        OptimizelyDecideOption.IGNORE_USER_PROFILE_SERVICE
      ]
      if (!isMounted()) {
        defaultDecideOptions.push(OptimizelyDecideOption.DISABLE_DECISION_EVENT)
      }

      return createInstance({
        datafile,
        logLevel: IS_DEBUG ? enums.LOG_LEVEL.DEBUG : enums.LOG_LEVEL.ERROR,
        eventDispatcher,
        defaultDecideOptions,
        // https://docs.developers.optimizely.com/full-stack/docs/event-batching-javascript#basic-example
        // on pages where have more than 10 events (i.e. /free-qr-code-generator) subsequent events are not sent to the
        // Optimizely backend. Increasing the batch size to 100 to be safe this doesn't creep up again until the underlying issue is fixed
        eventBatchSize: 100,
        eventFlushInterval: 1000
      })
    },
    [isMounted]
  )
}

const addHeapSession = (
  args: EventDispatcherArgs,
  heapSessionId: string,
  heapSessionIdEntityId?: string
): EventDispatcherArgs => {
  if (heapSessionIdEntityId) {
    const [event, cb] = args
    const params = event.params
    params.visitors = params.visitors.map((visitor: OptimizelyEvent['visitors'][0]) => {
      return {
        ...visitor,
        attributes: visitor.attributes.concat({
          entity_id: heapSessionIdEntityId,
          key: 'heapSessionId',
          type: 'custom',
          value: heapSessionId
        })
      }
    })

    const updatedEvent = {
      ...event,
      params
    }

    return [updatedEvent, cb]
  }
  return args
}

// Buffer data until we have GDPR opt-in and Heap session id
const useBufferedDispatcher = (): EventDispatcher => {
  const heapSessionId = useHeapSessionId()
  const isMounted = useIsMounted()
  const optimizelyContext = useOptimizelyContext()
  const { browserSessionIdLoaded } = useCookieState()
  const [pendingEvents, setPendingEvents] = useState<EventDispatcherArgs[]>([])
  const heapSessionIdEntityId = useMemo(() => {
    if (!optimizelyContext) return

    try {
      const { attributes }: { attributes: [{ id: string; key: string }] } = JSON.parse(
        optimizelyContext.datafile
      )

      const heapSessionIdAttr = attributes?.find(attr => attr.key === 'heapSessionId')
      if (!heapSessionIdAttr) {
        throw new Error('could not locate heapSessionId in datafile')
      }
      return heapSessionIdAttr.id
    } catch (e) {
      // ID value from `heapSessionId` https://app.optimizely.com/v2/projects/17283172040/audiences/attributes
      console.error(e)
    }
  }, [optimizelyContext])

  const eventDispatcher = useRef<EventDispatcher>({
    dispatchEvent: (): void => {
      //
    }
  })

  useEffect(() => {
    if (heapSessionId && browserSessionIdLoaded) {
      // dispatch buffered decision events
      if (pendingEvents.length) {
        pendingEvents.forEach(args => {
          const updatedArgs = addHeapSession(args, heapSessionId, heapSessionIdEntityId)
          _eventDispatcher.dispatchEvent(...updatedArgs)
        })
        setPendingEvents([])
      }

      eventDispatcher.current.dispatchEvent = (...args): void => {
        // modify event before forwarding
        const updatedArgs = addHeapSession(args, heapSessionId, heapSessionIdEntityId)
        _eventDispatcher.dispatchEvent(...updatedArgs)
      }
    } else {
      eventDispatcher.current.dispatchEvent = (...args): void => {
        if (isMounted()) {
          setPendingEvents(pendingEvents.concat([args]))
        }
      }
    }
  }, [pendingEvents, heapSessionId, browserSessionIdLoaded, heapSessionIdEntityId, isMounted])

  return eventDispatcher.current
}

const useBufferedTracking = (optimizely?: OptimizelyReactSDKClient): void => {
  useEffect(() => {
    if (!optimizely) return
    // emit track event for buffered track events
    if (Array.isArray(window.optimizelyTrack)) {
      window.optimizelyTrack.forEach((args: OptimizelyTrackArgs) => {
        optimizely.track(...args)
      })
    }

    // remove buffering
    window.optimizelyTrack = {
      push: (args: OptimizelyTrackArgs) => {
        return optimizely.track(...args)
      }
    }
  }, [optimizely])
}

const ORG_PLAN_PREFIX = 'org_plan:'
const PERSONAL_PLAN_PREFIX = 'personal_plan:'
function getFormattedPlanName(orgPlan: string, personalPlan: string): string | null {
  if (orgPlan && orgPlan.startsWith(ORG_PLAN_PREFIX)) {
    const plan = orgPlan.slice(ORG_PLAN_PREFIX.length)
    //needed to differentiate between old pro_plus (on personalPlan)
    if (plan === 'pro_plus') return 'pro_plus_org'
    return plan
  }
  if (personalPlan && personalPlan.startsWith(PERSONAL_PLAN_PREFIX)) {
    return personalPlan.slice(PERSONAL_PLAN_PREFIX.length)
  }
  return null
}

/**
 * Sets attributes used for targetting on the client.This can not be used for server side rendered feature flags.
 * @param optimizely
 */
const useClientAttributes = (optimizely?: OptimizelyReactSDKClient): void => {
  const jwt = useCurrentUserJwt()
  const organization = jwt?.org?.name?.toLowerCase()
  const ithacaId = jwt?.ithacaId
  const createdAt = jwt?.createdAt
  const email = jwt?.email
  const plan = getFormattedPlanName(jwt?.org?.orgPlan ?? '', jwt?.personalPlan ?? '')
  const orgId = jwt?.org?.orgId

  useEffect(() => {
    if (!optimizely) return

    optimizely.setUser({
      ...optimizely.user,
      attributes: {
        ...optimizely.user.attributes,
        // NOTE: passing undefined to optimizely triggers an error that creates
        // an infinite rerender loop - use null for clearing attributes
        email: email || null,
        account_type: plan || null,
        organization: organization || null,
        ithacaId: ithacaId || null,
        createdAt: +new Date(createdAt || 0),
        orgId: orgId || null
      }
    })
  }, [email, optimizely, organization, plan, ithacaId, createdAt, orgId])
}

export const useOptimizelyClient = (): OptimizelyReactSDKClient => {
  const optimizelyContext = useOptimizelyContext()
  const eventDispatcher = useBufferedDispatcher()
  const createOptimizely = useCreateOptimizely()

  const optimizely = useMemo(() => {
    if (!optimizelyContext) return
    const { sessionId, datafile, overrides = {}, attributes } = optimizelyContext
    const client = createOptimizely(datafile, eventDispatcher)
    if (!client) return
    client.setUser({ id: sessionId, attributes })
    Object.entries(overrides).forEach(([key, value]) => {
      client.setForcedVariation(key, sessionId, value)
    })
    return client
  }, [optimizelyContext, createOptimizely, eventDispatcher])

  useBufferedTracking(optimizely)
  useClientAttributes(optimizely)
  return optimizely
}
